// 現場設備分頁（STN-05 / NODE-04，SPEC #kinio-station-control）：Station 每個 PoE 孔的供電 / 電流 / 電壓 / 溫度 / link、學到的 MAC 與對到的 Thing，
// 單孔關閉 / 開啟 / 重新上電（kinio_station.poe_set，device.maintain + 二次確認）。只有 shadow 報了 kinio_station.poe_ports 才出現（DeviceDetailPage）。
// 沒報的欄位顯示「不知道」（IP808AR 溫度、IP1810 link / 速率還沒上板驗過，設備不報）；MAC 對 Thing 照 STN-05 規則（poe.ts macThings）。
// 能源管理（plan SPEC NRG-V5）：頂部「PoE 供電：已配置 90 / 120 W · 實際 46 W」；有 energy.read 時每孔加「今天 0.12 kWh」+ 24 小時迷你曲線（共用刻度），
// 點一孔展開 7 天曲線（排行點 PoE 列 → ?tab=channels&port=3#site 自動展開）。
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { can, canManage, type VmsDevice } from '../../lib/devices';
import { deviceCommand, stationRpc } from '../../lib/deviceApi';
import { useConfirm } from '../../components/ConfirmDialog';
import { macThings, poeOf, type ChannelRow, type PoePort } from './poe';
import { PortEnergyCell, PortWeek, useStationEnergy } from '../energy/DeviceEnergy';
import { useFmt } from '../energy/hooks';
import { pctFloor } from '../energy/format';

type Action = 'on' | 'off' | 'cycle';

export function PoePorts({ dev, reported, onChanged }: { dev: VmsDevice; reported?: Record<string, unknown>; onChanged: () => void }) {
  const { t } = useTranslation();
  const data = poeOf(reported);
  // 重開機、OTA 同級的維運動作（device.maintain；P1 owner / admin 隱含 = can_manage）
  const maintain = can(dev, 'device.maintain') || canManage(dev);
  // ch.list：通道的 MAC（對到這台的通道 Thing）。拿不到就只列 MAC，不擋其他內容
  const chans = useQuery({
    queryKey: ['station-ch-list', dev.apiEndpoint, dev.homeId, dev.aid],
    queryFn: () => stationRpc<ChannelRow[]>(dev, 'ch.list'),
    enabled: dev.presence !== 'offline' && !!data?.ports.some((p) => p.macs.length),
    staleTime: 60_000,
    retry: false,
  });
  const things = macThings(dev, Array.isArray(chans.data) ? chans.data : undefined);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ port: number; text: string; bad?: boolean } | null>(null);
  const { ask, dialog } = useConfirm();
  const energy = useStationEnergy(dev, !!data);
  const efmt = useFmt();
  const [sp] = useSearchParams();
  const [openPort, setOpenPort] = useState<number | null>(() => (sp.get('port') ? Number(sp.get('port')) : null));
  if (!data) return null;
  const ports = energy.data?.ports;
  const portE = new Map((ports ?? []).map((p) => [p.port, p]));
  // 迷你曲線共用刻度（同一台每孔可比較；設計稿 r1 IA P2-09）
  const sparkMax = Math.max(1, ...(ports ?? []).flatMap((p) => p.spark.filter((v): v is number => v !== null)));
  const unknown = t('poeports.unknown');
  const run = async (p: PoePort, action: Action) => {
    const ok = await ask({
      title: t(`poeports.confirm_${action}`, { port: p.port }),
      body: action === 'on' ? undefined : t('poeports.confirm_body'),
      action: t(`poeports.act_${action}`),
    });
    if (!ok) return;
    setBusy(p.port);
    setMsg(null);
    try {
      // cycle = 關、等 5 秒、開，ack 在重新開回之後才回 → 等久一點
      const o = await deviceCommand(dev.apiEndpoint, dev.homeId, dev.aid, 'kinio_station.poe_set', { port: p.port, action }, action === 'cycle' ? 25 : 10);
      const r = (o?.result ?? null) as { status?: string; payload?: { code?: string; message?: string } } | null;
      if (o?.status !== 'acked') throw new KinioError(504, 'COMMAND_TIMEOUT', '');
      if (r?.status !== 'ok') throw new KinioError(502, r?.payload?.code || r?.status?.toUpperCase() || 'DEVICE_ERROR', r?.payload?.message || '');
      setMsg({ port: p.port, text: t('poeports.done', { port: p.port }) });
      onChanged();
    } catch (e) {
      const c = e instanceof KinioError ? e.code : String(e);
      setMsg({ port: p.port, text: t(`poeports.err_${c}`, { defaultValue: t('poeports.err_other', { code: c }) }), bad: true });
    } finally {
      setBusy(null);
    }
  };
  // 沒報的值一律淡色「不知道」（不顯示成 0）
  const unk = <span className="muted">{unknown}</span>;
  const fmt = (v: number | undefined, unit: string, digits = 0) => (v === undefined ? unk : `${v.toFixed(digits)} ${unit}`);
  const b = data.budget;
  return (
    <div data-testid="poe-ports">
      <p className="muted small">{t('poeports.intro')}</p>
      {b && (
        <p data-testid="poe-budget">
          <b>
            {!b.ratedW
              ? t('energy:stn.budget_norated', { used: efmt.watts(b.usedW, 1) })
              : b.allocatedW === undefined
                ? t('energy:stn.budget_noalloc', { rated: efmt.watts(b.ratedW, 0), used: efmt.watts(b.usedW, 1) })
                : t('energy:stn.budget', { alloc: efmt.watts(b.allocatedW, 1), rated: efmt.watts(b.ratedW, 0), used: efmt.watts(b.usedW, 1) }) +
                  `（${pctFloor(b.allocatedW, b.ratedW)}%）`}
          </b>
        </p>
      )}
      {b?.allocatedW !== undefined && b.usedW !== undefined && b.allocatedW - b.usedW >= 10 && (
        <p className="small muted" data-testid="poe-alloc-note">
          {t('energy:stn.alloc_note', { diff: efmt.watts(b.allocatedW - b.usedW, 0) })}
        </p>
      )}
      {data.ports.length === 0 ? (
        <p className="muted">{t('poeports.none')}</p>
      ) : (
        <div className="table-wrap">
          <table className="grid poe-table">
            <thead>
              <tr>
                <th scope="col">{t('poeports.col_port')}</th>
                <th scope="col">{t('poeports.col_power')}</th>
                <th scope="col">{t('poeports.col_meas')}</th>
                <th scope="col">{t('poeports.col_link')}</th>
                <th scope="col">{t('poeports.col_devices')}</th>
                {ports && <th scope="col">{t('energy:stn.col_energy')}</th>}
                {maintain && <th scope="col">{t('poeports.col_actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {data.ports.map((p) => (
                <Fragment key={p.port}>
                <tr data-port={p.port}>
                  <th scope="row">{p.port}</th>
                  <td>
                    <span className={`badge ${!p.enabled ? 'badge-warn' : p.powered ? 'badge-ok' : ''}`} data-testid="poe-state">
                      {!p.enabled ? t('poeports.st_disabled') : p.powered ? t('poeports.st_powered') : t('poeports.st_idle')}
                    </span>
                    {p.powered && <div className="small muted">{fmt(p.powerW, 'W', 2)}</div>}
                  </td>
                  <td className="small">
                    {p.powered ? (
                      <>
                        <div>
                          {fmt(p.currentMa, 'mA')} · {p.voltageMv === undefined ? unk : `${(p.voltageMv / 1000).toFixed(1)} V`}
                        </div>
                        <div className="muted">
                          {t('poeports.class')} {p.cls ?? unk} · {t('poeports.temp')} {fmt(p.tempC, '°C')}
                        </div>
                      </>
                    ) : (
                      <span className="muted">{t('poeports.temp')} {fmt(p.tempC, '°C')}</span>
                    )}
                  </td>
                  <td className="small">
                    {p.link === undefined ? unk : p.link === 'up' ? t('poeports.link_up') : t('poeports.link_down')}
                    {p.link === 'up' && <> · {p.speedMbps === undefined ? unk : `${p.speedMbps} Mbps`}</>}
                  </td>
                  <td className="small">
                    {p.macs.length === 0 && <span className="muted">—</span>}
                    <ul className="plain-list">
                      {p.macs.map((m) => {
                        const th = things.get(m);
                        return (
                          <li key={m} data-mac={m}>
                            <code>{m}</code>{' '}
                            {th ? (
                              <Link to={`/devices/${encodeURIComponent(th.homeId)}/${encodeURIComponent(th.aid)}`} data-testid="poe-thing">
                                <span className={`dot ${th.presence}`} /> {th.name} · {t(`devices.${th.presence}`)}
                              </Link>
                            ) : (
                              <span className="muted" data-testid="poe-foreign">
                                {t('poeports.not_home')}
                              </span>
                            )}
                          </li>
                        );
                      })}
                      {p.macsMore && <li className="muted">{t('poeports.macs_more')}</li>}
                    </ul>
                  </td>
                  {ports && (
                    <td>
                      {portE.get(p.port) ? (
                        <div className="en-root" style={{ display: 'grid', gap: 2, justifyItems: 'start' }}>
                          <PortEnergyCell port={p.port} today={portE.get(p.port)!.today_mwh} spark={portE.get(p.port)!.spark} vmax={sparkMax} fmt={efmt} />
                          <button
                            type="button"
                            className="link-btn small"
                            aria-expanded={openPort === p.port}
                            onClick={() => setOpenPort(openPort === p.port ? null : p.port)}
                            data-testid="poe-port-expand"
                          >
                            {openPort === p.port ? t('energy:stn.collapse') : t('energy:stn.expand', { port: p.port })}
                          </button>
                        </div>
                      ) : (
                        <span className="muted">{unknown}</span>
                      )}
                    </td>
                  )}
                  {maintain && (
                    <td>
                      <div className="toolbar wrap">
                        {p.enabled ? (
                          <>
                            {p.powered && (
                              <button className="btn btn-sm" disabled={busy !== null || dev.presence === 'offline'} onClick={() => void run(p, 'cycle')} data-act="cycle">
                                {t('poeports.act_cycle')}
                              </button>
                            )}
                            <button className="btn btn-sm btn-danger" disabled={busy !== null || dev.presence === 'offline'} onClick={() => void run(p, 'off')} data-act="off">
                              {t('poeports.act_off')}
                            </button>
                          </>
                        ) : (
                          <button className="btn btn-sm" disabled={busy !== null || dev.presence === 'offline'} onClick={() => void run(p, 'on')} data-act="on">
                            {t('poeports.act_on')}
                          </button>
                        )}
                      </div>
                      {busy === p.port && <span className="small muted">{t('poeports.sending')}</span>}
                      {msg?.port === p.port && (
                        <span className={`small ${msg.bad ? 'error' : 'muted'}`} role={msg.bad ? 'alert' : 'status'}>
                          {msg.text}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
                {openPort === p.port && portE.get(p.port) && (
                  <tr className="en-port-open">
                    <td colSpan={5 + (ports ? 1 : 0) + (maintain ? 1 : 0)}>
                      <PortWeek dev={dev} port={p.port} meter={portE.get(p.port)!.meter} name={portE.get(p.port)!.name} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {maintain && <p className="small muted">{t('poeports.not_persistent')}</p>}
      {dialog}
    </div>
  );
}
